import * as THREE from 'three';
import { C, SEASON_LEAF } from '../core/palette.js';
import { box, cyl, sphere, textSprite } from '../core/meshkit.js';
import { BreadBoi } from '../entities/breadBoi.js';
import { Townsperson, breadSlice, stickLegs } from '../entities/townsfolk.js';
import { createAmbient } from '../entities/ambient.js';
import { HUD } from '../ui/hud.js';
import { getCrumbs, addCrumbs, getLumber, spendLumber, setFlag, getFlag, getPark, hasFlower, hasTree, plantFlower, plantTree } from '../core/save.js';
import { hasFound } from '../core/collection.js';
import { advanceTime, isNight, getSeason } from '../core/world.js';
import { WeatherSystem, getWeatherState } from '../core/weather.js';
import { questsForGiver, acceptQuest, turnIn, isReady as isQuestReady, objectiveProgress, QUESTS, flagObjective } from '../core/quests.js';

// Toast Park — a green space north of the town square. Plant flowers in empty
// beds and trees in bare dirt circles. Gardener Glen hands out a quest to
// green the place up. Gary lands here at night.

export function createParkScene(){
  return () => new ParkScene();
}

const GROUND_R = 34;      // how far the lawn is drawn
const WALK_R   = 26;      // how far you may walk — the perimeter tree line stands here

const LAMP_ON  = 0xFFE9A8;
const LAMP_OFF = 0xD8D2C2;

const FLOWER_SPOTS = [
  { x: -8, z: -6 }, { x: -4, z: -8 }, { x: 4, z: -8 }, { x: 8, z: -6 },
  { x: -9, z: 0.5 }, { x: 9, z: 0.5 }, { x: -8, z: 6.5 }, { x: 8, z: 6.5 }
];
const TREE_SPOTS = [
  { x: -11, z: -3 }, { x: 11, z: -3 }, { x: -11, z: 5 }, { x: 11, z: 5 }
];

const FLOWER_COST = 10;
const TREE_COST = 25;

class ParkScene {
  constructor(){
    this.state = 'explore';
    this.panels = [];
    this.hotspots = []; // must exist before _buildBeds/_buildTrees push to it
    this.lamps = [];

    this.season = getSeason();
    this.leaf = SEASON_LEAF[this.season] || C.sprout;

    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(0xA8D98A);
    this.scene.fog = new THREE.Fog(0xA8D98A, 40, 96);

    this.camera = new THREE.PerspectiveCamera(48, window.innerWidth/window.innerHeight, 0.1, 200);
    this.camDist = 20;
    this.camTarget = new THREE.Vector3(0, 1.5, 0);
    this.azimuth = 0; this.polar = 0.5;

    this._buildLights();
    this._buildGround();
    this._buildPerimeter();
    this._buildPond();
    this._buildBeds();
    this._buildTrees();

    this.bread = new BreadBoi();
    this.bread.root.position.set(0, 0, 16);
    this.bread.root.rotation.y = Math.PI;
    this.scene.add(this.bread.root);

    this.gardener = this._makeGardener();
    this.scene.add(this.gardener.root);

    this.gary = this._makeGary();
    this.gary.root.visible = isNight();
    this.scene.add(this.gary.root);

    this.ship = this._buildShipwreck();
    this.ship.visible = isNight();
    this.scene.add(this.ship);

    this.ambient = createAmbient({ center: new THREE.Vector3(0, 0, 0), radius: 22, butterflies: 6, birds: 3, fireflies: 6 });
    this.scene.add(this.ambient.group);

    this.weather = new WeatherSystem(this.scene, { center: new THREE.Vector3(0, 0, 0), radius: WALK_R });
    this._applyWeather();

    this.hotspots.push(
      { pos: this.gardener.root.position.clone(), r: 2.6, label: '🗣 talk to Gardener Glen', action: () => this._talkToGardener() },
      { pos: this.gary.root.position.clone(), r: 2.6, label: '🗣 talk to Gary', action: () => this._talkToGary(), kind: 'gary' },
      { pos: this.ship.position.clone(), r: 3.0, label: '🚀 fly to deep space', action: () => this.manager.goTo('deepSpace', { from: 'park' }), kind: 'ship', hidden: true, interactive: false },
      { pos: new THREE.Vector3(0, 0, 20), r: 2.8, label: '← back to Toast Town', action: () => this.manager.goTo('town', { from: 'park' }) }
    );
    this._updateGarySpot();
    this.nearSpot = null;
    this._updateLamps();
  }

  _buildLights(){
    this.scene.add(new THREE.AmbientLight(0xFFF6E8, 0.7));
    const shift = { spring: { fog: 0xA8E0A0 }, summer: { fog: 0xA8D98A }, autumn: { fog: 0xD4C68A }, winter: { fog: 0xDDEAE0 } }[this.season];
    if (shift) this.scene.fog.color.setHex(shift.fog);
    const sun = new THREE.DirectionalLight(0xFFF2D8, 0.85);
    sun.position.set(8, 20, 10); sun.castShadow = true;
    sun.shadow.mapSize.set(2048, 2048);
    const s = 38;
    sun.shadow.camera.left = -s; sun.shadow.camera.right = s;
    sun.shadow.camera.top = s; sun.shadow.camera.bottom = -s;
    this.scene.add(sun);
  }

  _buildGround(){
    // one ground material: the open green lawn
    const grass = new THREE.Mesh(new THREE.CircleGeometry(GROUND_R, 64), new THREE.MeshLambertMaterial({ color: C.grass }));
    grass.rotation.x = -Math.PI/2; grass.receiveShadow = true; this.scene.add(grass);

    // one path material: from town and around the pond
    const path = new THREE.Mesh(new THREE.PlaneGeometry(4.6, 16), new THREE.MeshLambertMaterial({ color: C.path }));
    path.rotation.x = -Math.PI/2; path.position.set(0, 0.01, 12); this.scene.add(path);

    const ring = new THREE.Mesh(new THREE.RingGeometry(5.6, 6.2, 56), new THREE.MeshLambertMaterial({ color: C.path }));
    ring.rotation.x = -Math.PI/2; ring.position.set(0, 0.01, 0); this.scene.add(ring);

    // two benches
    this._bench(new THREE.Vector3(-4.8, 0, 4.8), Math.PI/4);
    this._bench(new THREE.Vector3(4.8, 0, 4.8), -Math.PI/4);
  }

  // The town hub uses a perimeter tree line to enclose the plaza; the Park is the
  // one zone that *should* feel like an open green disc, but the clamp still has
  // to agree with the ground. A tree line exactly on the walk radius does both.
  _buildPerimeter(){
    const COUNT = 72;
    for (let i = 0; i < COUNT; i++){
      const a = (i / COUNT) * Math.PI * 2 + Math.sin(i * 4.3) * 0.25;
      // leave a gap on the south axis for the town exit
      const southExit = Math.abs(Math.sin(a)) < 0.18 && Math.cos(a) > 0;
      if (southExit) continue;

      const r = WALK_R - 1.0 + Math.cos(i * 3.7) * 1.4;
      const x = Math.sin(a) * r;
      const z = Math.cos(a) * r;
      const s = 1.0 + Math.sin(i * 2.1) * 0.35;
      this._perimeterTree(x, z, s);
    }

    // lamps along the main paths
    this._lamp(new THREE.Vector3(-2.5, 0, 17));
    this._lamp(new THREE.Vector3(0, 0, 7));
    this._lamp(new THREE.Vector3(-5.5, 0, 0));
    this._lamp(new THREE.Vector3(5.5, 0, 0));
    this._lamp(new THREE.Vector3(-9, 0, -7));
    this._lamp(new THREE.Vector3(9, 0, -7));
  }

  _perimeterTree(x, z, s){
    const g = new THREE.Group(); g.position.set(x, 0, z); g.scale.setScalar(s); this.scene.add(g);
    cyl(0.18, 0.24, 1.6, C.rye, 0, 0.8, 0, g, 8);
    cyl(0, 1.2, 1.8, this.leaf, 0, 2.3, 0, g, 10);
    cyl(0, 0.85, 1.4, this._darkLeaf(), 0, 3.3, 0, g, 10);
  }

  _darkLeaf(){
    const base = new THREE.Color(this.leaf);
    base.multiplyScalar(0.82);
    return base.getHex();
  }

  _bench(pos, rot){
    const g = new THREE.Group(); g.position.copy(pos); g.rotation.y = rot; this.scene.add(g);
    box(1.8, .14, .55, C.crustDark, 0, .55, 0, g);
    box(1.8, .45, .1, C.crustDark, 0, .78, -.26, g);
    cyl(.07, .07, .55, 0x5A4A2A, -.72, .28, 0, g, 6);
    cyl(.07, .07, .55, 0x5A4A2A,  .72, .28, 0, g, 6);
  }

  _buildPond(){
    const pond = new THREE.Mesh(new THREE.CircleGeometry(4.8, 36), new THREE.MeshLambertMaterial({ color: C.breeze }));
    pond.rotation.x = -Math.PI/2; pond.position.set(0, 0.02, 0); this.scene.add(pond);
    // reeds
    for (let i = 0; i < 12; i++){
      const a = i / 12 * Math.PI * 2;
      const r = 4.6 + Math.random() * .35;
      cyl(.04, .02, .7 + Math.random() * .5, C.sproutDim, Math.cos(a)*r, .35, Math.sin(a)*r, this.scene, 5);
    }
  }

  _lamp(pos){
    const g = new THREE.Group();
    g.position.copy(pos);
    this.scene.add(g);
    cyl(0.12, 0.16, 0.3, C.stone, 0, 0.15, 0, g, 10);
    cyl(0.06, 0.06, 3.2, C.cocoa, 0, 1.75, 0, g, 8);
    box(0.3, 0.3, 0.3, C.crustDark, 0, 3.55, 0, g);
    const glass = sphere(0.24, LAMP_ON, 0, 3.55, 0, g, 12);
    glass.material = new THREE.MeshBasicMaterial({ color: LAMP_OFF });
    const light = new THREE.PointLight(LAMP_ON, 0, 9, 1.7);
    light.position.set(0, 3.5, 0); g.add(light);
    this.lamps.push({ light, glass });
  }

  _updateLamps(){
    const night = isNight();
    this.lamps.forEach(l => {
      l.light.intensity = night ? 2.4 : 0;
      l.glass.material.color.set(night ? LAMP_ON : LAMP_OFF);
    });
  }

  _buildBeds(){
    this.flowerGroups = [];
    FLOWER_SPOTS.forEach((s, i) => {
      const g = new THREE.Group(); g.position.set(s.x, 0, s.z); this.scene.add(g);
      // dark soil bed
      box(1.8, .12, 1.8, 0x5C4033, 0, .06, 0, g);
      // small wood border
      const b = 0.95;
      box(1.9, .1, .1, C.rye, 0, .05,  b, g);
      box(1.9, .1, .1, C.rye, 0, .05, -b, g);
      box(.1, .1, 1.9, C.rye,  b, .05, 0, g);
      box(.1, .1, 1.9, C.rye, -b, .05, 0, g);

      if (hasFlower(i)) this._flowerPlant(g);
      this.flowerGroups.push({ index: i, group: g, planted: hasFlower(i) });

      this.hotspots.push({ pos: new THREE.Vector3(s.x, 0, s.z), r: 1.9, label: hasFlower(i) ? '🌷 flower bed' : '🌱 plant flowers', kind: 'flower', idx: i, interactive: !hasFlower(i) });
    });
  }

  _flowerPlant(g){
    const colors = [C.raspberry, C.tangerine, 0xB88FC4, 0xF6D336];
    for (let i = 0; i < 5; i++){
      const x = (Math.random() - .5) * .9;
      const z = (Math.random() - .5) * .9;
      cyl(.035, .035, .3, C.sproutDim, x, .22, z, g, 5);
      sphere(.14, colors[(i + Math.abs(Math.floor(x*10))) % colors.length], x, .4, z, g, 8);
    }
  }

  _buildTrees(){
    this.treeGroups = [];
    TREE_SPOTS.forEach((s, i) => {
      const g = new THREE.Group(); g.position.set(s.x, 0, s.z); this.scene.add(g);
      // bare dirt circle
      const dirt = new THREE.Mesh(new THREE.CircleGeometry(1.15, 22), new THREE.MeshLambertMaterial({ color: 0x5C4033 }));
      dirt.rotation.x = -Math.PI/2; dirt.position.y = .02; g.add(dirt);
      if (hasTree(i)) this._treePlant(g);
      this.treeGroups.push({ index: i, group: g, planted: hasTree(i) });

      this.hotspots.push({ pos: new THREE.Vector3(s.x, 0, s.z), r: 2.1, label: hasTree(i) ? '🌳 tree' : '🌱 plant a tree', kind: 'tree', idx: i, interactive: !hasTree(i) });
    });
  }

  _treePlant(g){
    cyl(.2, .26, 1.2, C.rye, 0, .6, 0, g, 8);
    cyl(0, 1.1, 1.7, this.leaf, 0, 1.9, 0, g, 10);
    cyl(0, 0.8, 1.2, this._darkLeaf(), 0, 2.7, 0, g, 10);
  }

  _makeGardener(){
    const build = (g) => {
      breadSlice(g, C.crust, C.toast, 0.62, 1.1);
      sphere(.07, 0x2E2018, -.16, 1.18, .3, g, 8);
      sphere(.07, 0x2E2018,  .16, 1.18, .3, g, 8);
      // sun hat
      const hat = new THREE.Group(); hat.position.set(0, 1.72, 0); g.add(hat);
      cyl(.52, .52, .06, 0xE8D6AE, 0, 0, 0, hat, 18);
      cyl(.28, .3, .32, 0xE8D6AE, 0, .19, 0, hat, 16);
      // overalls
      box(.6, .7, .36, 0x3D5FC0, 0, .45, 0, g);
      box(.15, .7, .38, 0x3D5FC0, -.23, .45, 0, g);
      box(.15, .7, .38, 0x3D5FC0,  .23, .45, 0, g);
      stickLegs(g, .5);
      // watering can
      const can = new THREE.Group(); can.position.set(.55, .5, .25); can.rotation.z = -.25; g.add(can);
      cyl(.12, .16, .3, 0x8A9A9A, 0, 0, 0, can, 10);
      cyl(.03, .03, .22, 0x8A9A9A, .18, .1, 0, can, 6);
    };
    return new Townsperson({
      name: 'Gardener Glen', line: 'every town needs a little green.',
      pos: new THREE.Vector3(4, 0, 6), facing: Math.PI * .8,
      wander: 0, speed: 0, bubbleY: 2.6, build
    });
  }

  _makeGary(){
    const build = (g) => {
      // little purple alien body
      sphere(.28, 0x8E5AC8, 0, .7, 0, g, 14);
      cyl(.12, .14, .45, 0x8E5AC8, 0, .36, 0, g, 12); // torso
      cyl(.04, .04, .32, 0x8E5AC8, -.22, .52, .1, g, 6);
      cyl(.04, .04, .32, 0x8E5AC8,  .22, .52, .1, g, 6);
      cyl(.06, .04, .42, 0x8E5AC8, -.1, .12, 0, g, 6);
      cyl(.06, .04, .42, 0x8E5AC8,  .1, .12, 0, g, 6);
      // big eyes
      sphere(.09, 0xFFFFFF, -.1, .78, .22, g, 10);
      sphere(.09, 0xFFFFFF,  .1, .78, .22, g, 10);
      sphere(.04, 0x2E2018, -.1, .78, .28, g, 8);
      sphere(.04, 0x2E2018,  .1, .78, .28, g, 8);
      // antennae
      cyl(.015, .015, .25, 0x8E5AC8, -.08, 1.05, 0, g, 6);
      cyl(.015, .015, .25, 0x8E5AC8,  .08, 1.05, 0, g, 6);
      sphere(.04, 0xB88FC4, -.08, 1.18, 0, g, 8);
      sphere(.04, 0xB88FC4,  .08, 1.18, 0, g, 8);
    };
    return new Townsperson({
      name: 'Gary', line: 'my ship! her hyperdrive coughed out again...',
      pos: new THREE.Vector3(-7, 0, -5), facing: Math.PI / 4,
      wander: 0, speed: 0, bubbleY: 2.8, build, schedule: 'night'
    });
  }

  _buildShipwreck(){
    const g = new THREE.Group(); g.position.set(-8, 0, -6);
    // saucer hull
    cyl(1.5, 2.0, .5, 0x7A8A9A, 0, .25, 0, g, 20);
    cyl(.85, 1.25, .6, 0x9AA8B8, 0, .7, 0, g, 20);
    // dome
    sphere(.65, 0xA0D0E0, 0, 1.15, 0, g, 16);
    // spark
    const spark = sphere(.07, 0xFFE9A8, 1.1, .7, .35, g, 6);
    spark.material = new THREE.MeshBasicMaterial({ color: 0xFFE9A8 });
    g.userData.spark = spark;
    return g;
  }

  _talkToGary(){
    this.bread.stop();
    const to = new THREE.Vector3().subVectors(this.gary.root.position, this.bread.root.position);
    this.bread.facing = Math.atan2(to.x, to.z); this.bread.root.rotation.y = this.bread.facing;
    this.bread.drawFace('grin');
    this._renderGaryDialog();
  }

  _renderGaryDialog(){
    this._clearPanels();
    const { available, active } = questsForGiver('gary');
    const rows = [];
    active.forEach(id => {
      const q = QUESTS[id];
      // Gating the hand-over on isQuestReady() would mean it never appears: this
      // quest's objective flag (garyShipFixed) is raised by _onGaryTurnIn — the
      // handler behind this very button. The objective is to *return the
      // stabilizer*, so the thing that makes it offerable is holding one.
      const canHandOver = id === 'garys-stabilizer' ? hasFound('lunar-stabilizer') : isQuestReady(id);
      if (canHandOver){
        rows.push(`<div class="q-drow ready"><div><b>${q.title}</b><span>you have the lunar stabilizer!</span></div>
          <button class="cafe-btn turnin" data-id="${id}">give it to Gary ✓</button></div>`);
      } else {
        rows.push(`<div class="q-drow"><div><b>${q.title}</b><span>${q.objective.noun}</span></div><span class="q-dpend">in progress</span></div>`);
      }
    });
    available.forEach(id => {
      const q = QUESTS[id];
      rows.push(`<div class="q-drow"><div><b>${q.title}</b><span>${q.desc} · 🍞 ${q.reward?.crumbs||0}</span></div>
        <button class="cafe-btn accept" data-id="${id}">accept</button></div>`);
    });
    const fixed = getFlag('garyShipFixed');
    const extra = fixed ? '<p class="q-dnone">Gary\'s ship is humming again. The stars are waiting.</p>' : '';
    const body = rows.length ? rows.join('') + extra : `<p class="q-dnone">"my ship! her hyperdrive coughed out again..."</p>${extra}`;

    const p = this._panel(`
      <div class="cafe-card q-dialog">
        <h2>Gary</h2>
        <div class="cafe-sub">stranded lunar trader</div>
        <div class="q-drows">${body}</div>
        <button class="cafe-btn ghost close">done</button>
      </div>`);
    p.querySelectorAll('.accept').forEach(b => b.onclick = () => { acceptQuest(b.dataset.id); HUD.refreshQuests(); this._renderGaryDialog(); });
    p.querySelectorAll('.turnin').forEach(b => b.onclick = () => this._onGaryTurnIn(b.dataset.id));
    p.querySelector('.close').onclick = () => this._clearPanels();
  }

  _onGaryTurnIn(id){
    if (!hasFound('lunar-stabilizer')){ HUD.say('You don\'t have the lunar stabilizer yet. Ask the Moon King.'); return; }
    setFlag('garyShipFixed', true);
    const reward = turnIn(id);
    if (!reward) return;
    HUD.refreshCrumbs(); HUD.refreshQuests();
    this.bread.drawFace('yum');
    HUD.say('Gary slots the lunar stabilizer into his ship. The spark fades and the console lights up!');
    this._updateGarySpot();
    this._renderGaryDialog();
  }

  _updateGarySpot(){
    const fixed = getFlag('garyShipFixed');
    if (this.ship.userData.spark) this.ship.userData.spark.visible = !fixed;
    const shipSpot = this.hotspots.find(h => h.kind === 'ship');
    if (shipSpot){ shipSpot.hidden = !fixed; shipSpot.interactive = fixed; }
  }

  // ---- gardener quest dialog ----
  _talkToGardener(){
    this.bread.stop();
    const to = new THREE.Vector3().subVectors(this.gardener.root.position, this.bread.root.position);
    this.bread.facing = Math.atan2(to.x, to.z);
    this.bread.root.rotation.y = this.bread.facing;
    this.bread.drawFace('grin');
    this._renderGardenerDialog();
  }

  _renderGardenerDialog(){
    this._clearPanels();
    const { available, active } = questsForGiver('gardener');
    const questRows = [];
    active.forEach(id => {
      const q = QUESTS[id];
      if (isQuestReady(id)){
        questRows.push(`<div class="q-drow ready"><div><b>${q.title}</b><span>objective complete!</span></div>
          <button class="cafe-btn turnin" data-id="${id}">turn in ✓</button></div>`);
      } else {
        questRows.push(`<div class="q-drow"><div><b>${q.title}</b><span>${q.objective.noun}</span></div><span class="q-dpend">in progress</span></div>`);
      }
    });
    available.forEach(id => {
      const q = QUESTS[id];
      const rew = q.reward?.crumbs ? ` · reward 🍞 ${q.reward.crumbs}` : '';
      questRows.push(`<div class="q-drow"><div><b>${q.title}</b><span>${q.desc}${rew}</span></div>
        <button class="cafe-btn accept" data-id="${id}">accept</button></div>`);
    });
    const body = questRows.length ? questRows.join('') : '<p class="q-dnone">"The park is looking lovely. Thanks for helping out!"</p>';

    const p = this._panel(`
      <div class="cafe-card q-dialog">
        <h2>Gardener Glen</h2>
        <div class="cafe-sub">toast park</div>
        <div class="q-drows">${body}</div>
        <button class="cafe-btn ghost close">done</button>
      </div>`);
    p.querySelectorAll('.accept').forEach(b => b.onclick = () => { acceptQuest(b.dataset.id); this.bread.drawFace('yum'); this._renderGardenerDialog(); });
    p.querySelectorAll('.turnin').forEach(b => b.onclick = () => this._onTurnIn(b.dataset.id));
    p.querySelector('.close').onclick = () => this._clearPanels();
  }

  _onTurnIn(id){
    const reward = turnIn(id);
    if (!reward) return;
    HUD.refreshCrumbs();
    this.bread.drawFace('yum');
    this._renderGardenerDialog();
  }

  // ---- planting ----
  _interactSpot(spot){
    this.bread.stop();
    if (spot.kind === 'flower') this._plantFlower(spot.idx);
    else this._plantTree(spot.idx);
  }

  _plantFlower(idx){
    if (hasFlower(idx)) return;
    this._clearPanels();
    const p = this._panel(`
      <div class="cafe-card q-dialog">
        <h2>🌷 Plant flowers</h2>
        <div class="cafe-sub">flower bed #${idx + 1}</div>
        <p class="q-dnone">Brighten the park for 🍞 ${FLOWER_COST}.</p>
        <button class="cafe-btn plant ${getCrumbs() >= FLOWER_COST ? '' : 'disabled'}">plant (🍞 ${FLOWER_COST})</button>
        <button class="cafe-btn ghost close">never mind</button>
      </div>`);
    p.querySelector('.plant').onclick = () => {
      if (getCrumbs() < FLOWER_COST) return;
      addCrumbs(-FLOWER_COST);
      plantFlower(idx);
      this._refreshSpot('flower', idx);
      this._checkGreenThumb();
      this._clearPanels();
      HUD.refreshCrumbs();
      this.bread.drawFace('grin');
      HUD.say('Planted a bed of flowers!');
      advanceTime(0.25);
    };
    p.querySelector('.close').onclick = () => this._clearPanels();
  }

  _plantTree(idx){
    if (hasTree(idx)) return;
    this._clearPanels();
    const afford = getCrumbs() >= TREE_COST && getLumber() >= 1;
    const p = this._panel(`
      <div class="cafe-card q-dialog">
        <h2>🌳 Plant a tree</h2>
        <div class="cafe-sub">tree spot #${idx + 1}</div>
        <p class="q-dnone">Plant a sapling for 🍞 ${TREE_COST} + 1 🪵.</p>
        <button class="cafe-btn plant ${afford ? '' : 'disabled'}">plant (🍞 ${TREE_COST} + 🪵 1)</button>
        <button class="cafe-btn ghost close">never mind</button>
      </div>`);
    p.querySelector('.plant').onclick = () => {
      if (!(getCrumbs() >= TREE_COST && getLumber() >= 1)) return;
      addCrumbs(-TREE_COST);
      spendLumber(1);
      plantTree(idx);
      this._refreshSpot('tree', idx);
      this._checkGreenThumb();
      this._clearPanels();
      HUD.refreshCrumbs();
      HUD.refreshLumber();
      this.bread.drawFace('grin');
      HUD.say('Planted a tree!');
      advanceTime(0.5);
    };
    p.querySelector('.close').onclick = () => this._clearPanels();
  }

  _refreshSpot(kind, idx){
    if (kind === 'flower'){
      const spot = this.flowerGroups[idx];
      spot.planted = true;
      this._flowerPlant(spot.group);
      const h = this.hotspots.find(h => h.kind === 'flower' && h.idx === idx);
      if (h){ h.label = '🌷 flower bed'; h.interactive = false; }
    } else {
      const spot = this.treeGroups[idx];
      spot.planted = true;
      this._treePlant(spot.group);
      const h = this.hotspots.find(h => h.kind === 'tree' && h.idx === idx);
      if (h){ h.label = '🌳 tree'; h.interactive = false; }
    }
    // hide any stale prompt now that this spot is just decoration
    if (this.nearSpot && this.nearSpot.kind === kind && this.nearSpot.idx === idx){
      this.nearSpot = null;
      HUD.showEnter(null);
    }
  }

  _applyWeather(){
    const st = getWeatherState();
    this.weather.set(st.weather, st.season);
  }

  _checkGreenThumb(){
    const park = getPark();
    const flowers = Object.keys(park.flowers).length;
    const trees = Object.keys(park.trees).length;
    if (flowers >= 3 && trees >= 1 && !getFlag('greenThumbComplete')){
      setFlag('greenThumbComplete', true);
      flagObjective('green-thumb', 'greenThumbComplete');
    }
  }

  _panel(html, mode = 'panel'){
    const el = document.createElement('div');
    el.className = mode === 'bare' ? 'cafe-panel bare' : 'cafe-panel';
    el.innerHTML = html;
    document.body.appendChild(el);
    this.panels.push(el);
    return el;
  }
  _clearPanels(){ this.panels.forEach(p => p.remove()); this.panels = []; }

  // ---- lifecycle ----
  enter({ manager }){
    this.manager = manager;
    this.input = manager.input;
    this.input.useCamera(this.camera);
    this.input.polarRange = [0.28, 0.72];
    this.input.onTap = (ndc, ray) => this._tap(ray);
    HUD.setSubtitle('toast park');
    HUD.say('Toast Park! Plant flowers and trees, or talk to Gardener Glen. At night, look for Gary near his ship.');
    HUD.setHint('click to walk · click folks to talk · walk up to a flower bed, tree spot, or Gardener Glen');
    HUD.hideHintSoon(9000);
    this._updateLamps();
  }

  exit(){ this.input.onTap = null; HUD.showEnter(null); HUD.say(''); this._clearPanels(); }

  _tap(ray){
    // clicking an NPC shows their speech bubble
    if (this._checkNpcClick(ray)) return;

    // check planting spots second
    const hit = this._raycastSpot(ray);
    if (hit){ this._interactSpot(hit); return; }

    const dir = ray.ray.direction;
    if (Math.abs(dir.y) < 1e-4) return;
    const t = -ray.ray.origin.y / dir.y;
    if (t <= 0) return;
    const p = ray.ray.origin.clone().addScaledVector(dir, t);
    const gp = this._clampToPark(new THREE.Vector3(p.x, 0, p.z));
    HUD.hideHint();
    this.bread.walkTo(gp);
  }

  _clampToPark(p){
    if (p.length() > WALK_R) p.setLength(WALK_R);
    return p;
  }

  _checkNpcClick(ray){
    const targets = [this.gardener, this.gary]
      .filter(n => n.root.visible)
      .map(n => n.hitTarget)
      .filter(Boolean);
    const hits = ray.intersectObjects(targets, false);
    if (hits.length){
      const npc = hits[0].object.userData.npc;
      if (npc){ this.bread.stop(); npc.showLine(); return true; }
    }
    return false;
  }

  _raycastSpot(ray){
    const dir = ray.ray.direction;
    if (Math.abs(dir.y) < 1e-4) return null;
    const t = -ray.ray.origin.y / dir.y;
    if (t <= 0) return null;
    const p = ray.ray.origin.clone().addScaledVector(dir, t);
    let best = null, bd = Infinity;
    this.hotspots.forEach(h => {
      if (!h.kind || h.interactive === false || h.hidden) return;
      const d = Math.hypot(p.x - h.pos.x, p.z - h.pos.z);
      if (d < 1.6 && d < bd){ bd = d; best = h; }
    });
    return best;
  }

  update(dt){
    this.bread.update(dt);
    this._clampToPark(this.bread.root.position);
    this.gardener.update(dt, this.bread.root.position);

    // Gary and his ship only appear at night
    const garyVisible = isNight();
    if (this.gary.root.visible !== garyVisible) this.gary.root.visible = garyVisible;
    if (this.ship.visible !== garyVisible) this.ship.visible = garyVisible;
    const garySpot = this.hotspots.find(h => h.kind === 'gary');
    if (garySpot) garySpot.hidden = !garyVisible;
    if (garyVisible) this.gary.update(dt, this.bread.root.position);

    const night = isNight();
    this.ambient.update(dt, night ? 'night' : 'day');
    this.weather.update(dt, this.bread.root.position);
    this._updateLamps();

    let near = null, nd = Infinity;
    this.hotspots.forEach(h => {
      if (h.interactive === false || h.hidden) return;
      const d = h.pos.distanceTo(this.bread.root.position);
      if (d < nd){ nd = d; near = h; }
    });
    if (near && nd < near.r){
      if (this.nearSpot !== near){
        this.nearSpot = near;
        HUD.showEnter(near.label, () => {
          if (near.action) near.action();
          else if (near.kind) this._interactSpot(near);
        });
      }
    } else if (this.nearSpot){ this.nearSpot = null; HUD.showEnter(null); }

    this.azimuth += (this.input.azimuthGoal - this.azimuth) * 0.08;
    this.polar   += (this.input.polarGoal   - this.polar)   * 0.08;
    this.camTarget.lerp(new THREE.Vector3(this.bread.root.position.x * 0.5, 1.6, this.bread.root.position.z * 0.5), 0.06);
    this.camera.position.set(
      this.camTarget.x + this.camDist * Math.sin(this.azimuth) * Math.cos(this.polar),
      this.camTarget.y + this.camDist * Math.sin(this.polar),
      this.camTarget.z + this.camDist * Math.cos(this.azimuth) * Math.cos(this.polar)
    );
    this.camera.lookAt(this.camTarget);
  }

  onResize(){
    this.camera.aspect = window.innerWidth / window.innerHeight;
    this.camera.updateProjectionMatrix();
  }
}
